import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Settings, Edit3, Eye, Trash2, Globe, Layout, Search, ExternalLink } from 'lucide-react';
import sitesApi from '../../services/sitesApi';
import { useSiteAuth } from '../../context/SiteAuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../context/ToastContext';

export default function SitesDashboard() {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewSiteModal, setShowNewSiteModal] = useState(false);
    const [newSiteName, setNewSiteName] = useState('');
    const [creating, setCreating] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const { siteUser, siteToken, loading: authLoading, siteLogout } = useSiteAuth();
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        if (!authLoading && !siteToken) {
            navigate('/sites/login');
        }
    }, [authLoading, siteToken, navigate]);

    useEffect(() => {
        if (siteToken) {
            fetchSites();
        }
    }, [siteToken]);

    const fetchSites = async () => {
        try {
            const res = await sitesApi.getSites();
            setSites(res.data.sites || res.data || []);
        } catch (err) {
            console.error('Failed to fetch sites', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSite = async (e) => {
        e.preventDefault();
        if (!newSiteName.trim()) return;
        setCreating(true);
        try {
            const res = await sitesApi.createSite({ name: newSiteName });
            const newSite = res.data.site || res.data;
            setShowNewSiteModal(false);
            navigate(`/sites/editor/${newSite.id || newSite._id}`);
        } catch (err) {
            console.error('Create site error:', err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to create site. Please try again.';
            toast.error(errMsg);
            setCreating(false);
        }
    };

    const handleDeleteSite = (id) => {
        setConfirmDeleteId(id);
    };

    const handleConfirmDelete = async () => {
        if (!confirmDeleteId) return;
        const id = confirmDeleteId;
        setConfirmDeleteId(null);
        try {
            await sitesApi.deleteSite(id);
            setSites(sites.filter(s => (s.id || s._id) !== id));
            toast.success('Site deleted successfully!');
        } catch (err) {
            console.error('Failed to delete site', err);
            toast.error('Failed to delete site.');
        }
    };

    if (authLoading || !siteToken) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-300">
            {/* Top Navigation */}
            <nav className="bg-slate-900/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2">
                            <Layout className="text-primary-500" size={24} />
                            <span className="text-xl font-bold text-white">Aaharam Sites</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <Link to="/sites/subscription" className="hidden md:block text-sm">
                                <span className="bg-primary-500/10 text-primary-400 px-3 py-1 rounded-full border border-primary-500/20 hover:bg-primary-500/20 transition-all cursor-pointer">
                                    Plan: {siteUser?.plan_name || 'Free'}
                                </span>
                            </Link>
                            <Link to="/sites/subscription" className="text-sm text-slate-400 hover:text-white transition-colors">
                                Billing
                            </Link>
                            <button onClick={siteLogout} className="text-sm text-slate-400 hover:text-white transition-colors">
                                Sign out
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">My Websites</h1>
                        <p className="text-slate-400">Manage and publish your restaurant websites.</p>
                    </div>
                    <button
                        onClick={() => navigate('/sites/templates')}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-medium transition-colors"
                    >
                        <Plus size={20} />
                        Create New Site
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                ) : sites.length === 0 ? (
                    <div className="backdrop-blur-sm bg-slate-900/40 border border-slate-800 rounded-3xl p-12 text-center mt-8">
                        <div className="mx-auto w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mb-6">
                            <Globe size={40} className="text-primary-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-3">No sites yet</h2>
                        <p className="text-slate-400 mb-8 max-w-md mx-auto">
                            Start building your digital presence. Create a beautiful website for your restaurant in minutes.
                        </p>
                        <button
                            onClick={() => navigate('/sites/templates')}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                        >
                            Browse Templates
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sites.map(site => {
                            const siteId = site.id || site._id;
                            const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                            const baseDomain = isLocal ? `localhost:${window.location.port || '5173'}` : 'aaharamcloud.com';
                            const siteUrl = `http://${site.slug}.${baseDomain}`;

                            return (
                                <div key={siteId} className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors group">
                                    <div className="h-40 bg-slate-950 relative flex items-center justify-center overflow-hidden">
                                        {/* Thumbnail placeholder */}
                                        {site.logo_url ? (
                                            <img src={site.logo_url} alt={`${site.name} Logo`} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300" />
                                        ) : (
                                            <Globe size={48} className="text-slate-700" />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent"></div>
                                        <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                                            <span className={`text-xs font-medium px-2 py-1 rounded-md ${site.is_published ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/20 text-amber-400 border border-amber-500/20'}`}>
                                                {site.is_published ? 'Published' : 'Draft'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <h3 className="text-lg font-bold text-white mb-1 truncate">{site.name}</h3>
                                        <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1 mb-4">
                                            {site.slug}.${baseDomain} <ExternalLink size={12} />
                                        </a>
                                        
                                        <div className="grid grid-cols-4 gap-2 pt-4 border-t border-slate-800">
                                            <button onClick={() => navigate(`/sites/editor/${siteId}`)} className="flex flex-col items-center justify-center p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="Edit Site">
                                                <Edit3 size={18} />
                                            </button>
                                            <button onClick={() => navigate(`/sites/${siteId}/settings`)} className="flex flex-col items-center justify-center p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="Settings">
                                                <Settings size={18} />
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    if (!site.is_published) {
                                                        toast.info('This website is currently in draft. Please open the editor and click "Publish" at least once to preview it live.');
                                                        return;
                                                    }
                                                    window.open(siteUrl, '_blank');
                                                }} 
                                                className="flex flex-col items-center justify-center p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" 
                                                title="Preview"
                                            >
                                                <Eye size={18} />
                                            </button>
                                            <button onClick={() => handleDeleteSite(siteId)} className="flex flex-col items-center justify-center p-2 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete">
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* New Site Modal */}
            {showNewSiteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <h3 className="text-xl font-bold text-white mb-4">Name your site</h3>
                        <form onSubmit={handleCreateSite}>
                            <div className="mb-6">
                                <label className="block text-sm text-slate-400 mb-2">Site Name</label>
                                <input
                                    type="text"
                                    autoFocus
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                                    placeholder="e.g. The Rustic Spoon"
                                    value={newSiteName}
                                    onChange={(e) => setNewSiteName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowNewSiteModal(false)}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                                >
                                    {creating ? 'Creating...' : 'Create Site'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Deletion Confirm Modal */}
            <ConfirmModal
                isOpen={!!confirmDeleteId}
                title="Delete Site"
                message="Are you sure you want to delete this site? This cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmDeleteId(null)}
                variant="danger"
            />
        </div>
    );
}
